/**
 * Building a document with Core command bodies outside the DocStore (CR-W2-14-5): documents that do not exist in a
 * store yet (the new-chart wizard, the sample project, anything built before it is stored) run their commands in
 * ONE TxDraft over a fresh container, exactly as the DocStore runs them, and the result must pass the full
 * invariant check.
 */

import { checkInvariants } from '../model/invariants';
import type { ChartDoc, InvariantViolation } from '../model/types';
import type { SelectionSnapshot, TxBuilder } from '../ops/types';
import type { MessageRef } from '../util/types';
import { createDocContainer } from './container';
import { deserializeDoc, serializeDoc } from './serialize';
import { createTxDraft } from './txDraft';

const EMPTY_SELECTION: SelectionSnapshot = Object.freeze({
  notes: [],
  keys: [],
  segments: [],
  lines: [],
  bpm: [],
  markers: [],
  effects: [],
  videos: [],
  groups: [],
  activeLine: null,
});

/** A command refused (`reason`) or the result broke model invariants (`violations`). */
export class DocBuildError extends Error {
  readonly reason: MessageRef | null;
  readonly violations: readonly InvariantViolation[];
  constructor(message: string, reason: MessageRef | null, violations: readonly InvariantViolation[]) {
    super(message);
    this.name = 'DocBuildError';
    this.reason = reason;
    this.violations = violations;
  }
}

/**
 * Runs `build` as ONE transaction (TxDraft over a fresh container, empty selection) over `doc`, applies it, runs the
 * full `checkInvariants` and returns an independent snapshot (serialize/deserialize) plus the build's value.
 * Throws DocBuildError.
 */
export function buildDocWithCore<R>(
  doc: ChartDoc,
  build: (tx: TxBuilder) => R,
): { readonly doc: ChartDoc; readonly value: R } {
  const container = createDocContainer(doc);
  const draft = createTxDraft(container, EMPTY_SELECTION);
  const run = draft.run(build);
  if (!run.ok) {
    throw new DocBuildError(
      `command failed: ${run.reason.key} ${JSON.stringify(run.reason.params ?? {})}`,
      run.reason,
      [],
    );
  }
  container.applyAll(draft.finish().ops);
  const violations = checkInvariants(container.doc, container.idx);
  if (violations.length > 0) {
    throw new DocBuildError(
      `invariants: ${violations.map((v) => `${v.code}: ${v.message}`).join('; ')}`,
      null,
      violations,
    );
  }
  return { doc: deserializeDoc(serializeDoc(container.doc)), value: run.value };
}
